import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useApi } from '../data';
import tracker from '../analytics/tracker';
import './operator.scss';

const preparePostalAddress = (d) => {
  const removeWords = (str, words) =>
    (str || '')
      .split(' ')
      .filter(
        (w) => !words.map((x) => x.toLowerCase()).includes(w.toLowerCase())
      )
      .join(' ');
  const removeDups = (str) => [...new Set(str.split(/\\s+/))].join(' ');
  const poles = ['East', 'West', 'North', 'South'];
  const boPo = ['H.O', 'S.O', 'B.O'];

  const mobile = d.donorPhone ? `, Mobile: ${d.donorPhone}` : '';
  const door = d.donorAddress_en ? `, ${d.donorAddress_en}` : '';
  const address = `${removeWords(d.village, poles)}, ${removeWords(d.taluk, poles)}, ${d.district || ''}, ${d.state || ''}`;
  return `${d.donorName_en || ''}${door}, ${removeDups(address)}, PO: ${removeWords(d.post_office, boPo)}, Pincode: ${d.pincode || ''}${mobile}`;
};

function Dashboard() {
  const { logout } = useAuth();
  const api = useApi();

  useEffect(() => {
    tracker.pageViewOperatorDashboard();
  }, []);
  const [query, setQuery] = useState('');
  const [donors, setDonors] = useState([]);
  const [receipts, setReceipts] = useState(null);
  const [selectedDonor, setSelectedDonor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState('');
  const [error, setError] = useState('');

  const clearResults = () => {
    setDonors([]);
    setReceipts(null);
    setSelectedDonor(null);
    setError('');
  };

  const handleQueryChange = (e) => {
    setQuery(e.target.value);
    if (!e.target.value.trim()) clearResults();
  };

  const handleSearch = useCallback(
    async (e) => {
      e.preventDefault();
      if (!query.trim()) return;
      setError('');
      setLoading(true);
      clearResults();
      try {
        const { data } = await api.authGet(
          `/portal/search?search=${query.trim()}`
        );
        setDonors(Array.isArray(data) ? data : []);
        tracker.operatorSearch(
          query.trim(),
          Array.isArray(data) ? data.length : 0
        );
        if (!data?.length) setError('No donors found');
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [query]
  );

  const viewReceipts = useCallback(async (donor) => {
    setSelectedDonor(donor);
    setLoading(true);
    tracker.operatorViewReceipts(donor.donorId);
    try {
      const { data } = await api.authGet(
        `/portal/receipts?userid=${donor.donorId}`
      );
      setReceipts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const downloadPdf = useCallback(async (invoiceNumber) => {
    setDownloading(invoiceNumber);
    tracker.operatorDownloadPdf(invoiceNumber);
    try {
      const blob = await api.authBlob(`/portal/download-pdf/${invoiceNumber}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading('');
    }
  }, []);

  return (
    <div className="op-page">
      <div className="op-header">
        <span className="op-header__title">Operator Portal</span>
        <button
          className="op-header__btn"
          onClick={() => {
            tracker.operatorLogout();
            logout();
          }}
        >
          Logout
        </button>
      </div>

      <div className="op-content">
        <form onSubmit={handleSearch} className="op-search">
          <input
            type="text"
            className="op-search__input"
            value={query}
            onChange={handleQueryChange}
            placeholder="Donor ID or Phone"
          />
          <button type="submit" className="op-search__btn" disabled={loading}>
            Search
          </button>
        </form>

        {error && <div className="op-error">{error}</div>}
        {loading && (
          <div className="op-loader">
            <span className="spinner-border spinner-border-sm"></span>
          </div>
        )}

        {!loading && !donors.length && !receipts && !error && (
          <div
            style={{
              textAlign: 'center',
              padding: '2rem 0',
              color: '#d4a528',
              fontSize: '0.8rem',
            }}
          >
            Search donors by ID or phone number
          </div>
        )}

        {/* Donor Cards */}
        {donors.length > 0 &&
          !receipts &&
          !loading &&
          donors.map((d) => (
            <div key={d.donorId} className="op-card">
              <div className="op-card__header">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <span className="op-card__badge">Donor ID: {d.donorId}</span>
                  <span className="op-card__type">{d.donorType}</span>
                </div>
              </div>
              <div className="op-field">
                <div className="op-field__label">Name</div>
                <div className="op-field__name">
                  {d.donorName_ta}
                  {d.donorName_en ? ` / ${d.donorName_en}` : ''}
                </div>
              </div>
              {d.donorPrintNameInBook_ta && (
                <div className="op-field">
                  <div className="op-field__label">Book Print</div>
                  <div className="op-field__value">
                    {d.donorPrintNameInBook_ta}
                  </div>
                </div>
              )}
              <div className="op-field">
                <div className="op-field__label">Phone</div>
                <div className="op-field__value">{d.donorPhone || 'N/A'}</div>
              </div>
              {(d.village || d.donorAddress_en) && (
                <div className="op-field">
                  <div className="op-field__label">Postal Address</div>
                  <div className="op-field__value">
                    {preparePostalAddress(d)}
                  </div>
                </div>
              )}
              <button className="op-btn-full" onClick={() => viewReceipts(d)}>
                View Receipts ({d.totalReceipts})
              </button>
            </div>
          ))}

        {/* Receipts */}
        {receipts && selectedDonor && !loading && (
          <div>
            <div className="op-back">
              <button className="op-back__btn" onClick={clearResults}>
                ←
              </button>
              <span className="op-back__title">
                {selectedDonor.donorName_ta || selectedDonor.donorName_en} (
                {selectedDonor.donorId})
              </span>
            </div>
            {Object.keys(receipts).length === 0 ? (
              <p
                style={{
                  textAlign: 'center',
                  color: '#d4a528',
                  fontSize: '0.85rem',
                }}
              >
                No receipts found
              </p>
            ) : (
              Object.entries(receipts).map(([invoiceNo, items]) => (
                <div key={invoiceNo} className="op-receipt">
                  <div className="op-receipt__header">
                    <span className="op-receipt__invoice">{invoiceNo}</span>
                    <button
                      className="op-receipt__pdf"
                      onClick={() => downloadPdf(invoiceNo)}
                      disabled={downloading === invoiceNo}
                    >
                      {downloading === invoiceNo ? '⏳' : '↓ PDF'}
                    </button>
                  </div>
                  {items.map((item, idx) => (
                    <div key={idx} className="op-receipt__item">
                      {item.productName_ta} — ₹{item.invoiceAmount} (
                      {item.invoiceDate?.split(' ')[0]})
                      {item.isInvoiceReversed === 'Y' && (
                        <span className="op-receipt__cancelled">
                          [Cancelled]
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
